import { SharedStorage } from '@amzn/awscat-portfolio-assessment-types';
import { Badge, SpaceBetween } from '@amzn/awsui-components-react';

import { constants } from '../../common/Localization';
import { ColumnDefinition, ColumnTypes } from '../../common/table/types';
import { getCell } from '../common/awsui-table-helper';
import { MetadataItem } from '../common/AWSUIFormBuilder';
import { excludeFromTCOToDisplayMap } from './NetworkItems';

export const defaultStorageMetadata: MetadataItem<SharedStorage>[] = [
    {
        identifier: 'storageDesc',
        type: 'string',
        label: 'Storage Description',
        uiHidden: false,
        uiDisabled: false
    },
    {
        identifier: 'storageType',
        type: 'enum',
        enum: [
            {
                value: 'SAN',
                text: 'SAN'
            },
            {
                value: 'NAS',
                text: 'NAS'
            },
            {
                value: 'Object',
                text: 'Object'
            }
        ],
        defaultValue: 'SAN',
        label: '* Storage Type',
        uiHidden: false,
        uiDisabled: false,
    },
    {
        identifier: 'diskType',
        type: 'enum',
        enum: [
            {
                value: 'HDD',
                text: 'HDD'
            },
            {
                value: 'SSD',
                text: 'SSD'
            }
        ],
        label: constants.DISK_TYPE,
        uiHidden: false,
        uiDisabled: false,
    },
    {
        identifier: 'storageCapacity',
        type: 'enum',
        enum: [
            {
                value: 'Raw',
                text: 'Raw'
            },
            {
                value: 'Usable',
                text: 'Usable'
            }
        ],
        defaultValue: 'Raw',
        label: 'Raw/Usable ',
        uiHidden: false,
        uiDisabled: false,
    },
    {
        identifier: 'sizeInGB',
        type: 'number',
        defaultValue: 1,
        label: '* Size in GB',
        uiHidden: false,
        uiDisabled: false,
        validation: {
            decimalPlaces: 0,
            min: 1,
            max: 9999999
        }
    },
    {
        identifier: 'storageSystem',
        type: 'enum',
        enum: [
            {
                value: 'EBS',
                text: 'EBS'
            },
            {
                value: 'EFS',
                text: 'EFS'
            }
        ],
        defaultValue: 'EBS',
        label: 'EBS/EFS',
        uiHidden: false,
        uiDisabled: true,
    },
    {
        identifier: 'objectService',
        type: 'enum',
        enum: [
            {
                value: 'S3Standard',
                text: 'S3Standard'
            },
            {
                value: 'S3Glacier',
                text: 'S3Glacier'
            }
        ],
        defaultValue: 'S3Standard',
        label: 'Standard/Glacier',
        uiHidden: true,
        uiDisabled: false,
    },
    {
        identifier: 'ebsVolumeType',
        type: 'enum',
        enum: [
            {
                value: 'General Purpose',
                text: 'General Purpose',
            },
            {
                value: 'General Purpose - gp2 only',
                text: 'General Purpose - gp2 only',
            },
            {
                value: 'General Purpose - gp3 only',
                text: 'General Purpose - gp3 only',
            },
            {
                value: 'Provisioned IOPS',
                text: 'Provisioned IOPS',
            }, {
                value: 'Throughput Optimized HDD',
                text: 'Throughput Optimized HDD',
            }, {
                value: 'Cold HDD',
                text: 'Cold HDD',
            }
        ],
        label: constants.EBS_VOLUME_TYPE,
        uiHidden: false,
        uiDisabled: false,
    },
    {
        identifier: 'maxIops',
        type: 'number',
        defaultValue: 200,
        label: 'Max IOPS',
        uiHidden: false,
        uiDisabled: false,
        validation: {
            decimalPlaces: 0,
            min: 0,
            max: 999999
        }
    },
    {
        identifier: 'accessedInfrequentlyPct',
        type: 'number',
        defaultValue: 0,
        label: 'Infrequent Access (%)',
        uiHidden: true,
        uiDisabled: false,
        validation: {
            decimalPlaces: 0,
            min: 0,
            max: 100
        }
    },
    {
        identifier: 'excludeFromTco',
        type: 'multiValueEnum',
        defaultValue: [],
        selections: [
            {
                value: 'aws',
                text: 'AWS',
                uiDisabled: false
            },
            {
                value: 'onPremises',
                text: 'On-Premises',
                uiDisabled: false
            }
        ],
        label: 'Exclude from TCO assessment',
        uiHidden: false,
        uiDisabled: false
    },
];

export const sharedStorageColumnDefinition: ColumnDefinition<SharedStorage>[] = [
    {
        id: 'storageDesc',
        header: constants.STORAGE_DESCRIPTION,
        cell: item => getCell(item.storageDesc),
        type: ColumnTypes.TEXT,
    },
    {
        id: 'storageType',
        header: constants.STORAGE_TYPE,
        cell: item => getCell(item.storageType),
        type: ColumnTypes.TEXT,
    },
    {
        id: 'diskType',
        header: constants.DISK_TYPE,
        cell: e => getCell(e.diskType),
        type: ColumnTypes.TEXT,
    },
    {
        id: 'storageCapacity',
        header: constants.STORAGE_CAPACITY,
        cell: e => getCell(e.storageCapacity),
        type: ColumnTypes.TEXT,
    },
    {
        id: 'sizeInGB',
        header: constants.STORAGE_SIZE_IN_GB,
        cell: item => getCell(item.sizeInGB),
        type: ColumnTypes.NUMBER,
    },
    {
        id: 'maxIops',
        header: constants.STORAGE_MAX_IOPS,
        cell: item => getCell(item.maxIops),
        type: ColumnTypes.NUMBER,
    },
    {
        id: 'storageSystem',
        header: constants.STORAGE_SYSTEM,
        cell: e => getCell(e.storageSystem),
        type: ColumnTypes.TEXT,
    },
    {
        id: 'ebsVolumeType',
        header: constants.EBS_VOLUME_TYPE,
        cell: e => getCell(e.ebsVolumeType),
        type: ColumnTypes.TEXT,
    },
    {
        id: 'objectService',
        header: constants.STORAGE_OBJECT_SERVICE,
        cell: e => getCell(e.objectService),
        type: ColumnTypes.TEXT,
    },
    {
        id: 'accessedInfrequentlyPct',
        header: constants.STORAGE_ACCESSED_INFREQUENTLY_PCT,
        cell: item => getCell(item.accessedInfrequentlyPct),
        type: ColumnTypes.NUMBER,
    },
    {
        id: 'excludeFromTco',
        header: constants.STORAGE_EXCLUDE_FROM_TCO,
        data: item => item.excludeFromTco.join(','),
        cell: item => item.excludeFromTco ? (
            <SpaceBetween direction='horizontal' size='s'>
                {item.excludeFromTco.map(exclude => (
                    <Badge
                        key={exclude}
                        color='blue'
                    >
                        {excludeFromTCOToDisplayMap.get(exclude)}
                    </Badge>
                ))}
            </SpaceBetween>
        ) : '-',
        type: ColumnTypes.CUSTOM,
    },
];
